import { useEffect, useState } from "react";
import { FiAlertTriangle, FiSlash, FiUserCheck } from "react-icons/fi";

import Modal from "newui/components/ui/Modal/Modal";
import { TextAreaWithLabel } from "newui/components/ui/InputWithLabel";
import { cn, showToast } from "utils/helpers";

import { RBAC_STRINGS } from "../../constants/rbacStrings";
import { useResolvePanelCoi } from "../../hooks/useEventGrantQueries";
import { parseRbacApiError } from "../../utils/rbacApiError";
import type { EventGrantCoi } from "../../types/eventGrant.type";

/**
 * Resolve a server-flagged conflict of interest for a seated panel member
 * (§5.3, Task 006 Rule 3). Two DISTINCT paths (AC2):
 *   - remove   → strips panel access; scores excluded.
 *   - override → retains the member; a documented reason is REQUIRED and is
 *                recorded to the audit trail (encrypted at rest, M5).
 *
 * The COI verdict is server-authoritative — the modal surfaces it verbatim and
 * never recomputes it. The resolve action is gated `can:resolveCoi`: when the
 * caller lacks the capability the actions are disabled and the reason is shown;
 * a server 403 is surfaced too (defence in depth).
 */

type CoiAction = "remove" | "override";
const MAX_REASON_LENGTH = 5000;

interface CoiResolutionModalProps {
	isOpen: boolean;
	rfxId: number;
	evalId: number;
	memberId: number;
	memberName: string;
	/** Server-authoritative COI verdict surfaced to the user. */
	coi?: EventGrantCoi | null;
	/** `can:resolveCoi` — server-authoritative capability, supplied by parent. */
	canResolve: boolean;
	onClose: () => void;
	onResolved?: () => void;
}

interface CoiActionOption {
	action: CoiAction;
	icon: React.ReactNode;
	label: string;
	help: string;
	testId: string;
}

const ACTION_OPTIONS: CoiActionOption[] = [
	{
		action: "remove",
		icon: <FiSlash size={18} aria-hidden="true" />,
		label: RBAC_STRINGS.coi.removeLabel,
		help: RBAC_STRINGS.coi.removeHelp,
		testId: "coi-action-remove",
	},
	{
		action: "override",
		icon: <FiUserCheck size={18} aria-hidden="true" />,
		label: RBAC_STRINGS.coi.overrideLabel,
		help: RBAC_STRINGS.coi.overrideHelp,
		testId: "coi-action-override",
	},
];

export const CoiResolutionModal = ({
	isOpen,
	rfxId,
	evalId,
	memberId,
	memberName,
	coi,
	canResolve,
	onClose,
	onResolved,
}: CoiResolutionModalProps) => {
	const mutation = useResolvePanelCoi(rfxId, evalId);

	const [action, setAction] = useState<CoiAction | null>(null);
	const [reason, setReason] = useState("");
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	useEffect(() => {
		if (isOpen) {
			setAction(null);
			setReason("");
			setErrorMessage(null);
		}
	}, [isOpen]);

	const reasonRequired = action === "override";
	const reasonValid = !reasonRequired || reason.trim().length > 0;
	const canSubmit =
		canResolve && action !== null && reasonValid && !mutation.isPending;

	const confirmLabel =
		action === "override"
			? RBAC_STRINGS.coi.confirmOverride
			: RBAC_STRINGS.coi.confirmRemove;

	const handleSubmit = (): void => {
		if (!canSubmit || action === null) {
			return;
		}
		setErrorMessage(null);
		mutation.mutate(
			{
				memberId,
				payload: {
					action,
					...(action === "override"
						? { override_reason: reason.trim() }
						: {}),
				},
			},
			{
				onSuccess: () => {
					showToast(
						action === "override"
							? RBAC_STRINGS.coi.overrideSuccess
							: RBAC_STRINGS.coi.removeSuccess,
						"Conflict resolved",
						true
					);
					onResolved?.();
					onClose();
				},
				onError: (err) => {
					const parsed = parseRbacApiError(err);
					if (parsed.status === 403) {
						setErrorMessage(RBAC_STRINGS.coi.unauthorised);
						return;
					}
					setErrorMessage(
						parsed.message ?? RBAC_STRINGS.coi.genericError
					);
				},
			}
		);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={RBAC_STRINGS.coi.title}
			size="md"
			iconType="warning"
			modalType="standard"
			closeOnEsc
			closeOnOverlayClick={false}
			primaryButton={{
				text: confirmLabel,
				onClick: handleSubmit,
				width: "full",
				disabled: !canSubmit,
				isLoading: mutation.isPending,
			}}
			secondaryButton={{
				text: RBAC_STRINGS.coi.cancel,
				onClick: onClose,
				width: "full",
			}}
		>
			<div className="space-y-4" data-testid="coi-resolution-modal">
				<div
					className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3"
					data-testid="coi-verdict"
				>
					<FiAlertTriangle
						size={16}
						className="mt-0.5 shrink-0 text-red-600"
						aria-hidden="true"
					/>
					<div className="text-sm text-red-700">
						<p className="font-medium">
							Conflict flagged for {memberName}
						</p>
						{coi?.status && (
							<p data-testid="coi-verdict-status">
								Status: {coi.status}
								{coi.rule != null ? ` · Rule ${coi.rule}` : ""}
							</p>
						)}
					</div>
				</div>

				<div className="space-y-2">
					{ACTION_OPTIONS.map((option) => {
						const isActive = action === option.action;
						return (
							<button
								key={option.action}
								type="button"
								onClick={() => setAction(option.action)}
								disabled={!canResolve}
								aria-pressed={isActive}
								className={cn(
									"flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors",
									isActive
										? "border-[#0031DD] bg-[#e8eeff]"
										: "border-gray-200 hover:bg-gray-50",
									!canResolve && "cursor-not-allowed opacity-60"
								)}
								data-testid={option.testId}
							>
								<span
									className={cn(
										"mt-0.5 shrink-0",
										isActive ? "text-[#0031DD]" : "text-gray-500"
									)}
								>
									{option.icon}
								</span>
								<span>
									<span className="block text-sm font-medium text-gray-800">
										{option.label}
									</span>
									<span className="block text-xs text-gray-500">
										{option.help}
									</span>
								</span>
							</button>
						);
					})}
				</div>

				{action === "override" && (
					<TextAreaWithLabel
						label={RBAC_STRINGS.coi.reasonLabel}
						required
						rows={3}
						maxLength={MAX_REASON_LENGTH}
						value={reason}
						placeholder={RBAC_STRINGS.coi.reasonPlaceholder}
						onChange={(e) => setReason(e.target.value)}
						testId="coi-override-reason"
						hasError={reasonRequired && reason.length > 0 && !reasonValid}
						errorText={
							reasonRequired && reason.length > 0 && !reasonValid
								? RBAC_STRINGS.coi.reasonRequired
								: undefined
						}
					/>
				)}

				{!canResolve && (
					<p
						className="text-sm text-gray-500"
						data-testid="coi-resolve-unauthorised"
					>
						{RBAC_STRINGS.coi.unauthorised}
					</p>
				)}

				{errorMessage && (
					<p
						className="text-sm text-red-600"
						role="alert"
						data-testid="coi-resolution-error"
					>
						{errorMessage}
					</p>
				)}
			</div>
		</Modal>
	);
};
